import { useEffect, useMemo, useState, type ComponentType } from 'react';
import { Link, Navigate, Route, Routes, useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  ArrowRight,
  BookOpen,
  Check,
  ChevronDown,
  ChevronRight,
  Command,
  FileText,
  Globe2,
  Layers3,
  Menu,
  Moon,
  Network,
  RadioTower,
  Search,
  ShieldCheck,
  Sun,
  X,
  Zap,
} from 'lucide-react';
import {
  articleBySlug,
  articles,
  articlesByTrack,
  categoriesByTrack,
  searchableText,
  tracks,
  type DocArticle,
  type Track,
  type TrackId,
} from './content';
import { MarkdownRenderer } from './MarkdownRenderer';

const productIcons: Record<TrackId, ComponentType<{ size?: number; strokeWidth?: number }>> = {
  aerix: Zap,
  beacon: RadioTower,
  orbit: Globe2,
};

const categoryIcons: Record<string, ComponentType<{ size?: number }>> = {
  Overview: BookOpen,
  'Setup and configuration': Layers3,
  'Traffic handling': Network,
  'Security and hardening': ShieldCheck,
  Operations: FileText,
  'Security and transport': ShieldCheck,
  'Resolver behavior': RadioTower,
  'Standalone mode': Layers3,
  'Cluster mode': Network,
  'Authoritative DNS': Globe2,
  'API and CLI': FileText,
  'Security and operations': ShieldCheck,
};

const productUrl = 'https://vcx.global';

const productPreface = (markdown: string) => {
  const lines = markdown.split(/\r?\n/);
  const result: string[] = [];
  for (const line of lines.slice(1)) {
    if (/^##\s+Contents\s*$/i.test(line) || /^##\s+/.test(line)) break;
    result.push(line);
  }
  return result.join('\n').trim();
};

function useTheme() {
  const [dark, setDark] = useState(() => {
    const saved = localStorage.getItem('vcx-docs-theme');
    return saved ? saved === 'dark' : window.matchMedia('(prefers-color-scheme: dark)').matches;
  });

  useEffect(() => {
    document.documentElement.dataset.theme = dark ? 'dark' : 'light';
    localStorage.setItem('vcx-docs-theme', dark ? 'dark' : 'light');
  }, [dark]);

  return { dark, setDark };
}

function useProgress() {
  const [completed, setCompleted] = useState<string[]>(() => {
    try {
      return JSON.parse(localStorage.getItem('vcx-docs-progress') ?? '[]');
    } catch {
      return [];
    }
  });

  const toggle = (slug: string) => {
    setCompleted((current) => {
      const next = current.includes(slug) ? current.filter((item) => item !== slug) : [...current, slug];
      localStorage.setItem('vcx-docs-progress', JSON.stringify(next));
      return next;
    });
  };

  return { completed, toggle };
}

function App() {
  const { dark, setDark } = useTheme();
  const progress = useProgress();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setMobileNavOpen(false);
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [location.pathname]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setSearchOpen(true);
      }
      if (event.key === 'Escape') {
        setSearchOpen(false);
        setMobileNavOpen(false);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  return (
    <div className="app-shell">
      <Header
        dark={dark}
        onTheme={() => setDark(!dark)}
        onMenu={() => setMobileNavOpen(true)}
        onSearch={() => setSearchOpen(true)}
      />
      <div className="workspace">
        <Sidebar completed={progress.completed} className={mobileNavOpen ? 'sidebar mobile-open' : 'sidebar'} />
        {mobileNavOpen && <button className="nav-backdrop" onClick={() => setMobileNavOpen(false)} aria-label="Close navigation" />}
        {mobileNavOpen && (
          <button className="mobile-nav-close icon-button" onClick={() => setMobileNavOpen(false)} aria-label="Close navigation">
            <X size={18} />
          </button>
        )}
        <main className="main-content">
          <Routes>
            <Route path="/" element={<DocsHome completed={progress.completed} />} />
            <Route path="/products/:trackId" element={<ProductPage completed={progress.completed} />} />
            <Route path="/docs/:slug" element={<ArticlePage completed={progress.completed} onToggleComplete={progress.toggle} />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </main>
      </div>
      {searchOpen && <SearchDialog onClose={() => setSearchOpen(false)} />}
    </div>
  );
}

function Header({
  dark,
  onTheme,
  onMenu,
  onSearch,
}: {
  dark: boolean;
  onTheme: () => void;
  onMenu: () => void;
  onSearch: () => void;
}) {
  return (
    <header className="topbar">
      <div className="brand">
        <button className="icon-button mobile-menu" onClick={onMenu} aria-label="Open navigation">
          <Menu size={19} />
        </button>
        <Link to="/" className="brand-link">
          <span className="brand-monogram">VCX</span>
          <span>VCX Global</span>
          <span className="brand-divider" />
          <span className="brand-section">Docs</span>
        </Link>
      </div>
      <button className="search-trigger" onClick={onSearch} aria-label="Search VCX documentation">
        <Search size={16} />
        <span>Search Aerix, Beacon, and Orbit documentation</span>
        <kbd><Command size={12} /> K</kbd>
      </button>
      <div className="topbar-actions">
        <button className="icon-button" onClick={onTheme} title={dark ? 'Use light mode' : 'Use dark mode'}>
          {dark ? <Sun size={18} /> : <Moon size={18} />}
        </button>
        <a href={productUrl} className="panel-link">VCX Global <ArrowRight size={15} /></a>
      </div>
    </header>
  );
}

function Sidebar({ completed, className }: { completed: string[]; className: string }) {
  const location = useLocation();
  const [openProducts, setOpenProducts] = useState<Record<TrackId, boolean>>({ aerix: true, beacon: true, orbit: true });

  return (
    <aside className={className}>
      <nav aria-label="Documentation navigation">
        <Link className={location.pathname === '/' ? 'nav-link active' : 'nav-link'} to="/">
          <BookOpen size={17} /> Documentation home
        </Link>
        <p className="nav-label">Software manuals</p>
        {tracks.map((product) => {
          const Icon = productIcons[product.id];
          const entries = articlesByTrack[product.id];
          const count = entries.filter((article) => completed.includes(article.slug)).length;
          const open = openProducts[product.id];
          return (
            <div key={product.id} className="service-nav-group product-nav-group">
              <button
                className={location.pathname === `/products/${product.id}` ? 'nav-link nav-toggle active' : 'nav-link nav-toggle'}
                onClick={() => setOpenProducts((current) => ({ ...current, [product.id]: !current[product.id] }))}
              >
                <Icon size={17} />
                <span>{product.title}</span>
                {count > 0 && <span className="nav-count">{count}/{entries.length}</span>}
                {open ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
              </button>
              {open && (
                <div className="service-nav">
                  <Link to={`/products/${product.id}`} className={location.pathname === `/products/${product.id}` ? 'service-link active' : 'service-link'}>
                    Product overview
                  </Link>
                  {categoriesByTrack[product.id].map((category) => (
                    <div key={category} className="service-nav-group">
                      <p>{category}</p>
                      {entries.filter((article) => article.category === category).map((article) => (
                        <Link
                          key={article.slug}
                          to={`/docs/${article.slug}`}
                          className={location.pathname === `/docs/${article.slug}` ? 'service-link active' : 'service-link'}
                        >
                          {completed.includes(article.slug) && <Check size={12} />}
                          {article.title}
                        </Link>
                      ))}
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </nav>
      <div className="sidebar-footer">
        <span>VCX documentation coverage</span>
        <strong>{articles.length} exhaustive sections</strong>
        <small>Aerix, Beacon, and Orbit source manuals fully indexed</small>
      </div>
    </aside>
  );
}

function DocsHome({ completed }: { completed: string[] }) {
  const nextArticle = articles.find((article) => !completed.includes(article.slug)) ?? articles[0];

  return (
    <div className="home-page">
      <section className="docs-intro">
        <div>
          <p className="eyebrow">docs.vcx.global</p>
          <h1>Master the VCX software stack.</h1>
          <p className="lead">
            Exhaustive operator documentation for Aerix, Beacon, and Orbit, organized into product manuals,
            deployment paths, security choices, configuration references, examples, and troubleshooting runbooks.
          </p>
          <div className="intro-actions">
            <Link to={`/docs/${nextArticle.slug}`} className="primary-button">
              {completed.length ? 'Continue learning' : 'Start reading'} <ArrowRight size={16} />
            </Link>
            <Link to="/products/orbit" className="secondary-button">Compare Orbit modes</Link>
          </div>
        </div>
        <div className="coverage-panel">
          <div><strong>3</strong><span>software manuals</span></div>
          <div><strong>{articles.length}</strong><span>indexed sections</span></div>
          <div><strong>{articles.reduce((sum, article) => sum + article.sections.length, 0)}</strong><span>deep subsections</span></div>
          <div><strong>{completed.length}</strong><span>completed</span></div>
        </div>
      </section>

      <section className="section-block">
        <div className="section-heading">
          <div>
            <p className="eyebrow">Choose a product</p>
            <h2>Three focused documentation sections</h2>
          </div>
          <p>Each product keeps the supplied markdown intact while adding navigation, search, reading progress, and practical grouping for operators.</p>
        </div>
        <div className="track-grid">
          {tracks.map((track, index) => (
            <ProductCard key={track.id} track={track} index={index + 1} completed={completed} />
          ))}
        </div>
      </section>

      <section className="section-band">
        <div className="section-heading">
          <div>
            <p className="eyebrow">Security choices</p>
            <h2>Find the critical decisions fast</h2>
          </div>
          <p>Security-relevant material is preserved in the product manuals and surfaced as direct entry points.</p>
        </div>
        <div className="service-directory">
          <DecisionGroup
            title="Aerix HTTP and HTTPS"
            icon={ShieldCheck}
            links={[
              articleBySlug.get('aerix-tls-and-http-3'),
              articleBySlug.get('aerix-site-config-reference'),
              articleBySlug.get('aerix-limits-and-protection'),
              articleBySlug.get('aerix-complete-examples'),
            ]}
          />
          <DecisionGroup
            title="Beacon resolver exposure"
            icon={RadioTower}
            links={[
              articleBySlug.get('beacon-dns-transports'),
              articleBySlug.get('beacon-tls-certificates'),
              articleBySlug.get('beacon-access-control-and-rate-limiting'),
              articleBySlug.get('beacon-dnssec-behavior'),
            ]}
          />
          <DecisionGroup
            title="Orbit deployment model"
            icon={Globe2}
            links={[
              articleBySlug.get('orbit-runtime-modes'),
              articleBySlug.get('orbit-cluster-operation'),
              articleBySlug.get('orbit-security-model'),
              articleBySlug.get('orbit-import-export-backup-and-migration'),
            ]}
          />
        </div>
      </section>

      <section className="start-banner">
        <div>
          <p className="eyebrow">Recommended path</p>
          <h2>Start with the product boundary, then read the exact operating mode.</h2>
          <p>Aerix centers on request handling and transport security. Beacon centers on resolver exposure and DNSSEC. Orbit centers on standalone versus leader/worker cluster operation.</p>
        </div>
        <Link to="/products/aerix" className="primary-button">Open Aerix <ArrowRight size={16} /></Link>
      </section>
    </div>
  );
}

function DecisionGroup({ title, icon: Icon, links }: { title: string; icon: ComponentType<{ size?: number }>; links: Array<DocArticle | undefined> }) {
  return (
    <div className="service-directory-group">
      <h3><Icon size={18} /> {title}</h3>
      {links.filter(Boolean).map((article) => (
        <Link to={`/docs/${article!.slug}`} key={article!.slug}>
          <span>{article!.title}</span><ChevronRight size={14} />
        </Link>
      ))}
    </div>
  );
}

function ProductCard({ track, index, completed }: { track: Track; index: number; completed: string[] }) {
  const Icon = productIcons[track.id];
  const trackArticles = articlesByTrack[track.id];
  const done = trackArticles.filter((article) => completed.includes(article.slug)).length;
  return (
    <Link to={`/products/${track.id}`} className="track-card">
      <div className="track-card-top">
        <span className="track-number">{String(index).padStart(2, '0')}</span>
        <Icon size={21} />
      </div>
      <h3>{track.title}</h3>
      <p>{track.description}</p>
      <div className="track-meta">
        <span>{trackArticles.length} sections</span>
        <span>{done ? `${done} complete` : track.audience}</span>
      </div>
      <div className="progress-line"><span style={{ width: `${trackArticles.length ? (done / trackArticles.length) * 100 : 0}%` }} /></div>
    </Link>
  );
}

function ProductPage({ completed }: { completed: string[] }) {
  const { trackId } = useParams();
  const product = tracks.find((item) => item.id === trackId);
  if (!product) return <Navigate to="/" replace />;
  const entries = articlesByTrack[product.id];
  const Icon = productIcons[product.id];
  const done = entries.filter((article) => completed.includes(article.slug)).length;

  return (
    <div className="track-page content-width">
      <Link to="/" className="back-link"><ArrowLeft size={15} /> Documentation home</Link>
      <header className="track-header">
        <div className="track-icon"><Icon size={24} /></div>
        <div>
          <p className="eyebrow">{product.source}</p>
          <h1>{product.title}</h1>
          <p>{product.description}</p>
        </div>
      </header>
      <div className="track-outcome">
        <div><strong>{done}/{entries.length}</strong><span>completed</span></div>
        <p><strong>Outcome:</strong> {product.outcome}</p>
      </div>

      <section className="preface-panel">
        <p className="eyebrow">Source introduction</p>
        <div className="article-body">
          <MarkdownRenderer markdown={productPreface(product.markdown)} />
        </div>
      </section>

      {product.id === 'orbit' && <OrbitModePlanner />}
      {product.id === 'aerix' && <AerixSecurityPlanner />}

      {categoriesByTrack[product.id].map((category) => (
        <section className="product-section" key={category}>
          <div className="section-heading compact">
            <div>
              <p className="eyebrow">{product.title}</p>
              <h2>{category}</h2>
            </div>
          </div>
          <div className="lesson-list">
            {entries.filter((article) => article.category === category).map((article, index) => (
              <ArticleRow key={article.slug} article={article} index={index} completed={completed.includes(article.slug)} />
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}

function OrbitModePlanner() {
  return (
    <section className="planner mode-planner">
      <div className="planner-heading">
        <div>
          <p className="eyebrow">Deployment modes</p>
          <h2>Standalone versus cluster mode</h2>
        </div>
        <Globe2 size={26} />
      </div>
      <div className="mode-grid">
        <Link to="/docs/orbit-runtime-modes#standalone">
          <strong>Standalone mode</strong>
          <span>One node handles DNS serving, REST management, CLI administration, and SQL storage. Start here for local tests, small deployments, or simple authoritative DNS operation.</span>
        </Link>
        <Link to="/docs/orbit-cluster-operation">
          <strong>Cluster mode</strong>
          <span>Leader nodes own writes and snapshots while worker nodes serve DNS from synced state. Use this when separating control-plane and serving-plane responsibilities.</span>
        </Link>
      </div>
    </section>
  );
}

function AerixSecurityPlanner() {
  return (
    <section className="planner mode-planner">
      <div className="planner-heading">
        <div>
          <p className="eyebrow">Transport security</p>
          <h2>HTTP, HTTPS, and HTTP/3 choices</h2>
        </div>
        <ShieldCheck size={26} />
      </div>
      <div className="mode-grid">
        <Link to="/docs/aerix-site-config-reference">
          <strong>HTTP site behavior</strong>
          <span>Use site-level routing, redirects, headers, CORS, rate limits, allow/deny rules, and request body limits to control cleartext entry points.</span>
        </Link>
        <Link to="/docs/aerix-tls-and-http-3">
          <strong>HTTPS and HTTP/3</strong>
          <span>Configure certificates, SNI, TLS versions, OCSP, mTLS, session behavior, QUIC, and HTTPS redirects for hardened public endpoints.</span>
        </Link>
      </div>
    </section>
  );
}

function ArticleRow({ article, index, completed }: { article: DocArticle; index: number; completed: boolean }) {
  return (
    <Link to={`/docs/${article.slug}`} className="lesson-row">
      <div className={completed ? 'lesson-index complete' : 'lesson-index'}>
        {completed ? <Check size={15} /> : String(index + 1).padStart(2, '0')}
      </div>
      <div className="lesson-copy">
        <div><span>{article.category}</span><span>{article.level}</span><span>{article.minutes} min</span></div>
        <h2>{article.title}</h2>
        <p>{article.summary}</p>
      </div>
      <ArrowRight size={18} />
    </Link>
  );
}

function ArticlePage({ completed, onToggleComplete }: { completed: string[]; onToggleComplete: (slug: string) => void }) {
  const { slug } = useParams();
  const article = slug ? articleBySlug.get(slug) : undefined;
  if (!article) return <Navigate to="/" replace />;
  const productEntries = articlesByTrack[article.track];
  const index = productEntries.findIndex((entry) => entry.slug === article.slug);
  const previous = index > 0 ? productEntries[index - 1] : undefined;
  const next = index < productEntries.length - 1 ? productEntries[index + 1] : undefined;
  const product = tracks.find((track) => track.id === article.track)!;
  const isComplete = completed.includes(article.slug);

  return (
    <div className="article-layout">
      <article className="article">
        <Link to={`/products/${article.track}`} className="back-link"><ArrowLeft size={15} /> {product.title}</Link>
        <header className="article-header">
          <div className="article-tags">
            <span>{article.category}</span><span>{article.level}</span><span>{article.minutes} min read</span>
          </div>
          <h1>{article.title}</h1>
          <p>{article.summary}</p>
          <div className="article-freshness">
            <span className="status-dot" /> Reviewed {article.reviewed}
            <span>{article.sourcePaths.join(', ')}</span>
          </div>
        </header>

        {article.sections.length > 0 && (
          <div className="mobile-toc">
            <strong>On this page</strong>
            {article.sections.map((item) => <a key={item.id} href={`#${item.id}`}>{item.title}</a>)}
          </div>
        )}

        <div className="article-body">
          <MarkdownRenderer markdown={article.markdown} />
        </div>

        <div className="article-complete">
          <div>
            <strong>{isComplete ? 'Section completed' : 'Finished this section?'}</strong>
            <span>Your progress is stored in this browser.</span>
          </div>
          <button className={isComplete ? 'complete-button done' : 'complete-button'} onClick={() => onToggleComplete(article.slug)}>
            <Check size={16} /> {isComplete ? 'Completed' : 'Mark complete'}
          </button>
        </div>

        <nav className="article-pagination">
          {previous ? <Link to={`/docs/${previous.slug}`}><ArrowLeft size={17} /><span><small>Previous</small>{previous.title}</span></Link> : <span />}
          {next ? <Link to={`/docs/${next.slug}`}><span><small>Next</small>{next.title}</span><ArrowRight size={17} /></Link> : <span />}
        </nav>
      </article>

      <aside className="article-aside">
        <p>On this page</p>
        {article.sections.length
          ? article.sections.map((item) => <a key={item.id} href={`#${item.id}`}>{item.title}</a>)
          : <a href="#top">{article.title}</a>}
        <div className="source-note">
          <strong>Documentation source</strong>
          <span>{article.sourcePaths.join(', ')}</span>
          <small>{article.reviewed}</small>
        </div>
      </aside>
    </div>
  );
}

function SearchDialog({ onClose }: { onClose: () => void }) {
  const [query, setQuery] = useState('');
  const navigate = useNavigate();
  const results = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    if (!normalized) return articles.slice(0, 10);
    return articles
      .map((article) => {
        const text = searchableText(article);
        const score = (article.title.toLowerCase().includes(normalized) ? 5 : 0)
          + (article.summary.toLowerCase().includes(normalized) ? 3 : 0)
          + (article.category.toLowerCase().includes(normalized) ? 2 : 0)
          + (text.includes(normalized) ? 1 : 0);
        return { article, score };
      })
      .filter((result) => result.score > 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, 12)
      .map((result) => result.article);
  }, [query]);

  const open = (article: DocArticle) => {
    onClose();
    navigate(`/docs/${article.slug}`);
  };

  return (
    <div className="search-overlay" onMouseDown={onClose}>
      <div className="search-dialog" onMouseDown={(event) => event.stopPropagation()}>
        <div className="search-input">
          <Search size={18} />
          <input autoFocus value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search configuration, security, APIs, transports, modes..." />
          <button onClick={onClose}>Esc</button>
        </div>
        <div className="search-results">
          <p>{query ? `${results.length} results` : 'Suggested documentation'}</p>
          {results.length ? results.map((article) => (
            <button key={article.slug} onClick={() => open(article)}>
              <span className="result-icon"><FileText size={16} /></span>
              <span><strong>{article.title}</strong><small>{tracks.find((track) => track.id === article.track)?.title} - {article.summary}</small></span>
              <ArrowRight size={15} />
            </button>
          )) : (
            <div className="empty-search"><Search size={24} /><strong>No matching documentation</strong><span>Try a software name, directive, endpoint, transport, or operating mode.</span></div>
          )}
        </div>
      </div>
    </div>
  );
}

export default App;
